import { parseFrontmatter, updateFrontmatter } from '@markbird/shared';

/**
 * A per-document word-count goal, kept in the note's own frontmatter as `goal`.
 *
 * Frontmatter rather than the local database, for the reason all of markbird's
 * note metadata lives there: it travels with the file. A goal set here survives
 * an export, a sync and a round trip through another editor.
 */

/** Largest goal accepted: generous for a book, small enough to catch a typo. */
export const MAX_WORD_GOAL = 1_000_000;

export function readWordGoal(markdown: string): number | null {
  const value = parseFrontmatter(markdown).data.goal;
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(n) && n > 0 && n <= MAX_WORD_GOAL ? n : null;
}

/**
 * Parse what someone typed into the goal prompt. `null` means "remove the goal" —
 * typed as 0, because the app's prompt treats an empty field as cancel;
 * `undefined` means the input is not a usable goal. Thousands separators are
 * tolerated, since "1,000" and "1.000" are how people write the number.
 */
export function parseGoalInput(input: string): number | null | undefined {
  const trimmed = input.trim().replace(/[\s.,’']/g, '');
  if (trimmed === '' || trimmed === '0') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  return n > 0 && n <= MAX_WORD_GOAL ? n : undefined;
}

/** The document with its goal set, or removed when `goal` is null. */
export function withWordGoal(markdown: string, goal: number | null): string {
  return updateFrontmatter(markdown, { goal: goal ?? undefined });
}
